import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Search, Building2, MoreHorizontal, Pencil, Trash2, X, ChevronRight, Sparkles, Users, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  aktiv: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  inaktiv: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400",
  lead: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
};

const emptyForm = { name: "", industry: "", description: "", contact_name: "", contact_email: "", contact_phone: "", website: "", address: "", city: "", status: "aktiv", notes: "", instagram: "", facebook: "", linkedin: "", tiktok: "", founding_year: "", employee_count: "", locations_count: "", achievements: "", specialties: "" };

export default function CompaniesPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [industryProfiles, setIndustryProfiles] = useState([]);

  const load = () => axios.get(`${API}/companies`, { withCredentials: true })
    .then(r => setCompanies(r.data))
    .catch(err => { if (err.response?.status !== 401) console.error(err); })
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  useEffect(() => {
    axios.get(`${API}/industry-profiles`, { withCredentials: true })
      .then(r => setIndustryProfiles(r.data || []))
      .catch(() => {});
  }, []);

  // Live-Detection: wenn der Nutzer in das Branchen-Feld tippt, suchen wir das passende Profil
  const detectedProfile = (() => {
    const q = (form.industry || "").toLowerCase().trim();
    if (!q || industryProfiles.length === 0) return null;
    return industryProfiles.find(p =>
      p.label.toLowerCase() === q ||
      p.key === q ||
      (p.keywords || []).some(kw => q.includes(kw))
    ) || null;
  })();

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.industry?.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/companies`, form, { withCredentials: true });
      toast.success("Firma angelegt");
      setShowModal(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Anlegen");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Firma "${name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/companies/${id}`, { withCredentials: true });
      toast.success("Firma gelöscht");
      load();
    } catch (err) {
      if (err.response?.status !== 401) toast.error("Fehler beim Löschen");
    }
  };

  const field = (key, label, type = "text", required = false) => (
    <div className="space-y-1">
      <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</Label>
      <Input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} required={required} className="rounded-sm" />
    </div>
  );

  return (
    <div className="space-y-5 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-0.5">Verwaltung</p>
          <h1 className="font-heading text-2xl font-black tracking-tight">Firmen</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="goto-leads-btn" onClick={() => navigate("/leads")} className="rounded-sm gap-1.5" size="sm">
            <Users size={14} /> Zu den Leads
          </Button>
        </div>
      </div>

      {/* Workflow-Hinweis */}
      <div className="flex items-start gap-2 px-4 py-3 bg-cyan-500/5 border border-cyan-500/20 rounded-sm text-xs">
        <Info size={14} className="text-cyan-500 shrink-0 mt-0.5" />
        <span className="text-muted-foreground">
          <strong className="text-foreground">Empfohlener Workflow:</strong> Lege Kontakte zuerst als <Link to="/leads" className="text-cyan-500 hover:underline">Lead</Link> an.
          Sobald qualifiziert oder die Demo gebaut ist, wird daraus automatisch eine Firma. So vermeidest du doppelte Eingaben und behältst die ganze Historie.
        </span>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Firma suchen..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9 rounded-sm"
          data-testid="company-search-input"
        />
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] text-xs font-semibold uppercase tracking-widest text-muted-foreground border-b border-border px-5 py-3 bg-muted/30">
          <span>Firma</span><span>Branche</span><span>Kontakt</span><span>Status</span><span></span>
        </div>
        {loading && <div className="px-5 py-8 text-sm text-muted-foreground text-center">Laden...</div>}
        {!loading && filtered.length === 0 && (
          <div className="px-5 py-12 text-center">
            <Building2 size={32} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">{search ? "Keine Treffer gefunden" : "Noch keine Firmen angelegt"}</p>
          </div>
        )}
        {filtered.map(c => (
          <div key={c.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] items-center px-5 py-3.5 border-b border-border last:border-0 hover:bg-muted/30 transition-colors" data-testid={`company-row-${c.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-sm bg-muted flex items-center justify-center text-xs font-bold shrink-0">
                {c.name[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <Link to={`/firmen/${c.id}`} className="text-sm font-medium text-foreground hover:text-blue-600 transition-colors truncate block">{c.name}</Link>
                {c.city && <p className="text-xs text-muted-foreground truncate">{c.city}</p>}
              </div>
            </div>
            <span className="text-sm text-muted-foreground truncate">{c.industry || "—"}</span>
            <span className="text-sm text-muted-foreground truncate">{c.contact_email || "—"}</span>
            <span className={`text-xs px-2 py-0.5 rounded-sm font-medium w-fit ${STATUS_MAP[c.status] || "bg-muted text-muted-foreground"}`}>{c.status}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid={`company-menu-${c.id}`} className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground">
                  <MoreHorizontal size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm">
                <DropdownMenuItem asChild>
                  <Link to={`/firmen/${c.id}`} className="flex items-center gap-2 text-sm"><ChevronRight size={13} /> Öffnen</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDelete(c.id, c.name)} className="text-destructive flex items-center gap-2 text-sm">
                  <Trash2 size={13} /> Löschen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Neue Firma anlegen</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4" data-testid="create-company-form">
            <div className="grid grid-cols-2 gap-4">
              {field("name", "Firmenname *", "text", true)}
              <div className="space-y-1">
                <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Branche
                  {detectedProfile && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary text-[10px] font-semibold normal-case tracking-normal"
                      data-testid="industry-detected-hint"
                    >
                      <Sparkles size={9} /> Profil erkannt: {detectedProfile.label}
                    </span>
                  )}
                </Label>
                <Input
                  list="industry-options"
                  value={form.industry}
                  onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                  placeholder="z.B. Friseur, Physiotherapie, Handwerk…"
                  className="rounded-sm"
                  data-testid="company-industry-input"
                />
                <datalist id="industry-options">
                  {industryProfiles.map(p => (
                    <option key={p.key} value={p.label} />
                  ))}
                </datalist>
                {detectedProfile && (
                  <p className="text-[10px] text-muted-foreground leading-tight">
                    Smart-Defaults bereit:&nbsp;
                    {detectedProfile.has_booking && "Buchungs-Widget · "}
                    {detectedProfile.has_inquiry && "Projekt-Anfrage · "}
                    {detectedProfile.suggested_pages_count} Unterseiten-Vorschläge
                  </p>
                )}
              </div>
              {field("contact_name", "Ansprechpartner")}
              {field("contact_email", "E-Mail", "email")}
              {field("contact_phone", "Telefon", "tel")}
              {field("website", "Website", "url")}
              {field("address", "Adresse")}
              {field("city", "Stadt")}
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Status</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="company-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aktiv">Aktiv</SelectItem>
                  <SelectItem value="inaktiv">Inaktiv</SelectItem>
                  <SelectItem value="lead">Lead</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Beschreibung</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="rounded-sm" rows={2} />
            </div>

            {/* Branding-DNA (Hebel F) — wirkt visuell auf die generierte Website */}
            <div className="border border-border rounded-sm px-3 py-3 space-y-3 bg-muted/20">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-primary" />
                <Label className="text-xs font-semibold uppercase tracking-wide text-foreground">
                  Branding-DNA <span className="text-[10px] text-muted-foreground font-normal normal-case tracking-normal ml-1">— bestimmt Typo, Farben & Vertrauen der Website</span>
                </Label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Gründungsjahr</Label>
                  <Input
                    value={form.founding_year}
                    onChange={e => setForm(f => ({ ...f, founding_year: e.target.value.replace(/\D/g,'').slice(0,4) }))}
                    placeholder="z.B. 1987"
                    className="rounded-sm h-8 text-sm"
                    data-testid="company-founding-year-input"
                    inputMode="numeric"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Mitarbeiter</Label>
                  <Input
                    value={form.employee_count}
                    onChange={e => setForm(f => ({ ...f, employee_count: e.target.value }))}
                    placeholder="z.B. 6 oder 10+"
                    className="rounded-sm h-8 text-sm"
                    data-testid="company-employee-count-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Standorte</Label>
                  <Input
                    value={form.locations_count}
                    onChange={e => setForm(f => ({ ...f, locations_count: e.target.value.replace(/\D/g,'').slice(0,3) }))}
                    placeholder="1"
                    className="rounded-sm h-8 text-sm"
                    data-testid="company-locations-count-input"
                    inputMode="numeric"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Spezialgebiete <span className="text-muted-foreground/70 normal-case tracking-normal">— kommasepariert · beeinflusst Farb-Vibe</span>
                </Label>
                <Input
                  value={form.specialties}
                  onChange={e => setForm(f => ({ ...f, specialties: e.target.value }))}
                  placeholder="z.B. Kinderphysio, manuelle Therapie, Sportrehabilitation"
                  className="rounded-sm h-8 text-sm"
                  data-testid="company-specialties-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Auszeichnungen &amp; Zertifikate <span className="text-muted-foreground/70 normal-case tracking-normal">— kommasepariert · zeigt Trust-Badges im Hero</span>
                </Label>
                <Input
                  value={form.achievements}
                  onChange={e => setForm(f => ({ ...f, achievements: e.target.value }))}
                  placeholder="z.B. Meisterbetrieb, IHK Premium-Siegel, ausgezeichnet 2019"
                  className="rounded-sm h-8 text-sm"
                  data-testid="company-achievements-input"
                />
              </div>
              {/* Live-Preview der DNA */}
              {(form.founding_year || form.specialties || form.employee_count || form.achievements) && (
                <div className="flex items-center gap-2 flex-wrap pt-1" data-testid="dna-preview">
                  {form.founding_year && /^\d+$/.test(form.founding_year) && (
                    (() => {
                      const y = parseInt(form.founding_year, 10);
                      const now = new Date().getFullYear();
                      const age = now - y;
                      const era = (y < 1995 || age >= 30) ? "heritage" : (y > 2018 ? "startup" : "default");
                      if (era === "default") return null;
                      return (
                        <span className={`text-[10px] px-2 py-0.5 rounded-sm font-medium ${era === "heritage" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300" : "bg-blue-500/15 text-blue-700 dark:text-blue-300"}`}>
                          {era === "heritage" ? "Heritage (Serif-Typo, warm)" : "Startup (Geo-Sans, kühl)"}
                        </span>
                      );
                    })()
                  )}
                  {(() => {
                    const h = `${form.specialties} ${form.description} ${form.name}`.toLowerCase();
                    if (/kinder|famili|baby|kids|kita/.test(h)) return <span className="text-[10px] px-2 py-0.5 rounded-sm font-medium bg-pink-500/15 text-pink-700 dark:text-pink-300">Playful (runde Formen)</span>;
                    if (/bio|öko|oeko|nachhaltig|vegan|grün|organic/.test(h)) return <span className="text-[10px] px-2 py-0.5 rounded-sm font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">Natural (Sage-Grün, Serif)</span>;
                    if (/luxus|premium|exklusiv|edel|gehoben/.test(h)) return <span className="text-[10px] px-2 py-0.5 rounded-sm font-medium bg-neutral-500/15 text-neutral-700 dark:text-neutral-300">Premium (reduziert, Serif)</span>;
                    return null;
                  })()}
                  {form.achievements && /ausgezeichnet|preis|award|meister|zertifik|gold|sieger/i.test(form.achievements) && (
                    <span className="text-[10px] px-2 py-0.5 rounded-sm font-medium bg-indigo-500/15 text-indigo-700 dark:text-indigo-300">Trust-Badges im Hero</span>
                  )}
                  {form.employee_count && parseInt(form.employee_count, 10) >= 5 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-sm font-medium bg-violet-500/15 text-violet-700 dark:text-violet-300">Team-Prominence</span>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Notizen</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="rounded-sm" rows={2} />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-sm">Abbrechen</Button>
              <Button type="submit" disabled={saving} className="rounded-sm" data-testid="create-company-submit-btn">
                {saving ? "Speichern..." : "Anlegen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
